/**
 * ProactiveInsightsPanel Component
 * Displays proactive AI insights that anticipate user needs
 * "Tell users what they need to know before they ask"
 */

import { useState, useEffect, useMemo } from 'react'
import {
  Bell,
  TrendingDown,
  TrendingUp,
  AlertOctagon,
  Clock,
  Calendar,
  Zap,
  Eye,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  X
} from 'lucide-react'
import clsx from 'clsx'

interface ProactiveInsight {
  id: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  category: 'anomaly' | 'trend' | 'prediction' | 'maintenance' | 'performance'
  title: string
  message: string
  assetId?: string
  timeframe: string
  metrics?: Array<{
    label: string
    value: string | number
    change?: number
    unit?: string
  }>
  suggestedAction?: {
    label: string
    onClick?: () => void
    href?: string
  }
  dismissable?: boolean
}

interface ProactiveInsightsPanelProps {
  insights: ProactiveInsight[]
  onDismiss?: (id: string) => void
  onActionClick?: (insight: ProactiveInsight) => void
  maxVisible?: number
  autoRotate?: boolean
  rotateInterval?: number
  className?: string
}

export default function ProactiveInsightsPanel({
  insights,
  onDismiss,
  onActionClick,
  maxVisible = 3,
  autoRotate = false,
  rotateInterval = 8000,
  className
}: ProactiveInsightsPanelProps) {
  const [expanded, setExpanded] = useState(false)
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())
  const [_currentRotation, setCurrentRotation] = useState(0)

  // Filter out dismissed insights
  const activeInsights = useMemo(() => {
    return insights.filter(i => !dismissedIds.has(i.id))
  }, [insights, dismissedIds])

  // Sort by priority
  const sortedInsights = useMemo(() => {
    const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 }
    return [...activeInsights].sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority])
  }, [activeInsights])

  // Auto-rotate effect
  useEffect(() => {
    if (!autoRotate || sortedInsights.length <= 1) return

    const interval = setInterval(() => {
      setCurrentRotation(prev => (prev + 1) % sortedInsights.length)
    }, rotateInterval)

    return () => clearInterval(interval)
  }, [autoRotate, rotateInterval, sortedInsights.length])

  const handleDismiss = (id: string) => {
    setDismissedIds(prev => new Set([...prev, id]))
    onDismiss?.(id)
  }

  const getCategoryIcon = (category: ProactiveInsight['category']) => {
    switch (category) {
      case 'anomaly':
        return <AlertOctagon size={16} />
      case 'trend':
        return <TrendingDown size={16} />
      case 'prediction':
        return <Eye size={16} />
      case 'maintenance':
        return <Clock size={16} />
      case 'performance':
        return <Zap size={16} />
      default:
        return <Bell size={16} />
    }
  }

  const getPriorityStyle = (priority: ProactiveInsight['priority']) => {
    switch (priority) {
      case 'critical':
        return {
          border: 'border-status-critical/50',
          bg: 'bg-status-critical/10',
          icon: 'text-status-critical',
          pulse: 'animate-pulse'
        }
      case 'high':
        return {
          border: 'border-status-warning/50',
          bg: 'bg-status-warning/10',
          icon: 'text-status-warning',
          pulse: ''
        }
      case 'medium':
        return {
          border: 'border-primary-500/50',
          bg: 'bg-primary-500/10',
          icon: 'text-primary-400',
          pulse: ''
        }
      default:
        return {
          border: 'border-surface-600',
          bg: 'bg-surface-800',
          icon: 'text-surface-400',
          pulse: ''
        }
    }
  }

  const visibleInsights = expanded ? sortedInsights : sortedInsights.slice(0, maxVisible)
  const hasMore = sortedInsights.length > maxVisible
  const criticalCount = sortedInsights.filter(i => i.priority === 'critical').length

  if (sortedInsights.length === 0) {
    return null
  }

  return (
    <div className={clsx('rounded-xl border border-surface-700 bg-surface-900 overflow-hidden', className)}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-surface-800/50 border-b border-surface-700">
        <div className="flex items-center gap-2">
          <div className={clsx(
            'p-1.5 rounded-lg',
            criticalCount > 0 ? 'bg-status-critical/20 text-status-critical' : 'bg-primary-500/20 text-primary-400'
          )}>
            <Bell size={16} className={criticalCount > 0 ? 'animate-pulse' : ''} />
          </div>
          <div>
            <h3 className="font-semibold text-surface-200 text-sm">Proactive Insights</h3>
            <p className="text-xs text-surface-400">
              {sortedInsights.length} item{sortedInsights.length !== 1 ? 's' : ''} need your attention
            </p>
          </div>
        </div>
        {criticalCount > 0 && (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-status-critical/20 text-status-critical animate-pulse">
            {criticalCount} Critical
          </span>
        )}
      </div>

      {/* Insights List */}
      <div className="divide-y divide-surface-800">
        {visibleInsights.map((insight, index) => {
          const style = getPriorityStyle(insight.priority)

          return (
            <div
              key={insight.id}
              className={clsx(
                'p-4 transition-all',
                style.bg,
                index === 0 && insight.priority === 'critical' && 'relative overflow-hidden'
              )}
            >
              {/* Critical pulse overlay */}
              {index === 0 && insight.priority === 'critical' && (
                <div className="absolute inset-0 bg-status-critical/5 animate-pulse pointer-events-none" />
              )}

              <div className="relative flex items-start gap-3">
                {/* Icon */}
                <div className={clsx('mt-0.5', style.icon, style.pulse)}>
                  {getCategoryIcon(insight.category)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-surface-200 text-sm">{insight.title}</h4>
                        {insight.assetId && (
                          <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-primary-500/10 text-primary-400">
                            {insight.assetId}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-surface-400 mt-0.5 flex items-center gap-1">
                        <Calendar size={10} />
                        {insight.timeframe}
                      </p>
                    </div>

                    {insight.dismissable && (
                      <button
                        onClick={() => handleDismiss(insight.id)}
                        className="p-1 text-surface-500 hover:text-surface-300 rounded transition-colors"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  <p className="text-sm text-surface-300 mt-2">{insight.message}</p>

                  {/* Metrics */}
                  {insight.metrics && insight.metrics.length > 0 && (
                    <div className="flex flex-wrap gap-4 mt-3">
                      {insight.metrics.map((metric, idx) => (
                        <div key={idx} className="text-xs">
                          <span className="text-surface-400">{metric.label}: </span>
                          <span className="font-mono text-surface-200">
                            {metric.value}{metric.unit}
                          </span>
                          {metric.change !== undefined && (
                            <span className={clsx(
                              'ml-1 inline-flex items-center',
                              metric.change >= 0 ? 'text-status-healthy' : 'text-status-critical'
                            )}>
                              {metric.change >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                              {Math.abs(metric.change)}%
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Suggested Action */}
                  {insight.suggestedAction && (
                    <button
                      onClick={() => {
                        insight.suggestedAction?.onClick?.()
                        onActionClick?.(insight)
                      }}
                      className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary-400 hover:text-primary-300 transition-colors"
                    >
                      {insight.suggestedAction.label}
                      <ExternalLink size={12} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Show More/Less */}
      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full p-3 flex items-center justify-center gap-2 text-sm text-surface-400 hover:text-surface-200 hover:bg-surface-800/50 transition-colors border-t border-surface-800"
        >
          {expanded ? (
            <>
              Show Less <ChevronUp size={16} />
            </>
          ) : (
            <>
              Show {sortedInsights.length - maxVisible} More <ChevronDown size={16} />
            </>
          )}
        </button>
      )}
    </div>
  )
}
